/**
 * GroupItem Sub-component
 *
 * A selectable item within the GroupedSelect dropdown.
 * When part of a group, displays a connecting dot indicator.
 * Items with children show a collapse/expand chevron.
 */

import { cn } from '@/shared/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Icon, IconKey, getIconComponent } from '../icon';
import type { GroupItemProps } from './types';

export function GroupItem({ item, isSelected, isInGroup = false, isExpanded, onToggleChildren }: GroupItemProps) {
  const { id, label, icon, color, disabled, depth = 0, hasChildren } = item;
  const IconComponent = icon ? getIconComponent(icon) : undefined;
  const ChevronIcon = getIconComponent(IconKey.ChevronRight)!;

  const selectItem = (
    <SelectPrimitive.Item
      value={id}
      disabled={disabled}
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-8 text-xs outline-none',
        'focus:bg-loan/10 text-text',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        isInGroup ? 'pl-3' : 'pl-8',
        isSelected && 'bg-primary/10 font-medium'
      )}
    >
      {/* Collapse/expand chevron for items with children */}
      {hasChildren && onToggleChildren && (
        <span
          role="button"
          tabIndex={-1}
          className="mr-1 flex h-4 w-4 shrink-0 items-center justify-center rounded hover:bg-muted/80 cursor-pointer"
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); onToggleChildren(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <ChevronIcon
            className={cn(
              'h-3 w-3 transition-transform duration-200',
              isExpanded && 'rotate-90'
            )}
          />
        </span>
      )}

      {/* Icon */}
      {IconComponent && (
        <span
          className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center"
          style={color ? { color } : undefined}
        >
          <IconComponent className="h-3.5 w-3.5" />
        </span>
      )}

      {/* Label */}
      <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>

      {/* Checkmark indicator */}
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Icon icon={IconKey.Check} size={16} />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );

  if (!isInGroup) return selectItem;

  // Sub-items (depth > 0): horizontal branch line from vertical border → dot → item
  if (depth > 0) {
    return (
      <div className="relative flex items-center" style={{ paddingLeft: `${depth * 1.25}rem` }}>
        {/* Horizontal branch line from the vertical border */}
        <span
          className="absolute top-1/2 left-0 border-t-2 border-border"
          style={{ width: `${depth * 1.25 - 0.25}rem` }}
        />
        {/* Dot at the end of the branch */}
        <span
          className={cn(
            'absolute h-2 w-2 shrink-0 rounded-full z-10 -translate-y-1/2 top-1/2',
            'border-2 border-border bg-loan',
            isSelected && 'border-primary bg-primary/30'
          )}
          style={{ left: `calc(${depth * 1.25}rem - 5px)` }}
        />
        <div className="ml-1.5 flex-1 min-w-0">
          {selectItem}
        </div>
      </div>
    );
  }

  // Direct children (depth 0): dot on the vertical border line
  return (
    <div className="relative flex items-center">
      <span
        className={cn(
          'absolute left-[-5px] h-2 w-2 shrink-0 rounded-full z-10',
          'border-2 border-border bg-loan',
          isSelected && 'border-primary bg-primary/30'
        )}
      />
      <div className="ml-1.5 flex-1 min-w-0">
        {selectItem}
      </div>
    </div>
  );
}
